import { useGetRecentData, useGetRecentDataBatched } from "api/api-hooks/datasets-hooks";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import { isEqual } from "lodash-es";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { APIResponse } from "services/HttpService";
import { ensureKeysWithUUID } from "utils/utils";
import { getUniversalTableDatasetValues } from "./universal-dataset-table-utils";
import "./UniversalDatasetTable.scss";
import UniversalDatasetTableHeaderRow from "./UniversalDatasetTableHeaderRow";
import UniversalDatasetTableRow from "./UniversalDatasetTableRow";
import { IUniversalDatasetTableConfig } from "./UniversalDatasetTableTypes";

export default function UniversalDatasetTable(props: IUniversalDatasetTableConfig) {
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);

  const lastDataRef = useRef<APIResponse<DatasetDataInfoResponse[]>[]>([]);
  const lastDatasetsRef = useRef<DatasetDataInfoResponse[]>([]);

  const datasetIds = useMemo(() => getUniversalTableDatasetValues(props.rows), [props.rows]);

  const onRowClickHandler = useCallback((index: number) => {
    setSelectedRowIndex(prev => (prev === index ? null : index));
  }, []);

  const { data: recentData } = useGetRecentDataBatched(datasetIds, {
    count: 1,
    refTime: props.referenceTime,
    options: {
      refetchInterval: props.updateFrequency ? props.updateFrequency * 1000 : 0,
    },
  });

  const { data: bgDataset } = useGetRecentData([props.backgroundColor?.dataset ?? ""], {
    count: 1,
    refTime: props.referenceTime,
    options: {
      enabled: !!props.backgroundColor?.dataset,
      refetchInterval: props.updateFrequency ? props.updateFrequency * 1000 : 0,
    },
  });

  const allDatasets = useMemo(() => {
    if (!isEqual(recentData, lastDataRef.current)) {
      lastDataRef.current = recentData;
      lastDatasetsRef.current = recentData.flatMap(batch => batch?.data ?? []);
    }
    return lastDatasetsRef.current;
  }, [recentData]);

  const rowsWithUUID = useMemo(() => {
    return ensureKeysWithUUID("uuid", props.rows);
  }, [props.rows]);

  const renderedRows = useMemo(() => {
    return rowsWithUUID.map((row, index) => {
      return (
        <UniversalDatasetTableRow
          key={row.uuid}
          rowIndex={index}
          recentData={allDatasets}
          rowData={row}
          columnsOptions={props.columnsOptions}
          compactMode={props.compactMode}
          maxAge={props.maxAge}
          isSelected={selectedRowIndex === index}
          onClick={onRowClickHandler}
        />
      );
    });
  }, [
    rowsWithUUID,
    allDatasets,
    props.columnsOptions,
    props.compactMode,
    props.maxAge,
    selectedRowIndex,
    onRowClickHandler,
  ]);

  const { bgColor, opacity } = useMemo(() => {
    if (
      props.backgroundColor &&
      props.backgroundColor.dataset &&
      props.backgroundColor.conditionalColors.length
    ) {
      const bgData = bgDataset?.[0]?.data;
      if (bgData) {
        const color = props.backgroundColor.conditionalColors.find(
          condition => condition.value === bgData[0].value
        );
        return { bgColor: color?.color || "transparent", opacity: color?.opacity || 0.1 };
      }
    }
    return { bgColor: "transparent", opacity: 0 };
  }, [bgDataset, props.backgroundColor]);

  return (
    <div className="main-main-window">
      {!props.reportMode && (
        <div
          className="absolute"
          style={{ inset: 0, backgroundColor: bgColor, opacity: opacity, zIndex: 0 }}
        />
      )}
      {props.rows.length > 0 && (
        <table className={`main-window ${props.reportMode ? "" : "bg-white"} relative z-1`}>
          <thead>
            {props.showColumnHeaders && !props.hideHeader && (
              <UniversalDatasetTableHeaderRow columnsOptions={props.columnsOptions} />
            )}
          </thead>
          <tbody className="universal-table-body">{renderedRows}</tbody>
        </table>
      )}
    </div>
  );
}
